import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/Button'
import { Affixed, Checkbox, Field, Input } from '@/components/ui/Field'
import { Page, PageHeader, Section } from '@/components/ui/Page'
import { ErrorState, Loading, Notice } from '@/components/ui/States'
import { cn } from '@/lib/cn'
import { describeError } from '@/lib/supabase'
import { useCurrentStaff } from '@/features/auth/RequireStaff'
import { settingsSchema, toRowUpdate, type SettingsFormValues } from './schema'
import { isUnconfigured, useSettings, useUpdateSettings } from './useSettings'

const TABS = [
  { to: '/settings', label: 'Business', end: true },
  { to: '/settings/team', label: 'Team', end: false },
  { to: '/settings/vendors', label: 'Vendors', end: false },
  { to: '/settings/venues', label: 'Venues', end: false },
  { to: '/settings/crew-terms', label: 'Crew terms', end: false },
  { to: '/settings/payment-plans', label: 'Payment plans', end: false },
  { to: '/settings/templates', label: 'Templates', end: false },
  { to: '/settings/theme', label: 'Theme', end: false },
  { to: '/settings/account', label: 'Your account', end: false },
]

export function SettingsLayout() {
  const location = useLocation()

  return (
    <Page>
      <PageHeader title="Settings" />

      <nav aria-label="Settings sections" className="-mx-5 flex gap-0 overflow-x-auto border-b px-5 lg:mx-0 lg:px-0 border-b-[var(--border-hairline)]">
        {TABS.map((tab) => {
          const active = tab.end ? location.pathname === tab.to : location.pathname.startsWith(tab.to)
          return (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.end}
              className={cn(
                'label -mb-px shrink-0 whitespace-nowrap border-b-2 px-3 py-2.5 transition-colors duration-[var(--dur-fast)]',
                active ? 'border-b-accent font-semibold text-accent' : 'border-b-transparent hover:text-ink',
              )}
            >
              {tab.label}
            </NavLink>
          )
        })}
      </nav>

      <Outlet />
    </Page>
  )
}

/**
 * The business settings form.
 *
 * Everything the studio knows about itself. Editable by the owner only; the
 * assistant sees the same screen read-only, because knowing the VAT rate is
 * part of doing the job and changing it is not.
 */
export function BusinessSettingsPage() {
  const { isOwner } = useCurrentStaff()
  const settingsQuery = useSettings()
  const update = useUpdateSettings()

  const form = useForm<SettingsFormValues>({
    resolver: zodResolver(settingsSchema),
    mode: 'onBlur',
  })

  const { register, handleSubmit, reset, watch, formState } = form
  const { errors, isDirty } = formState

  const settings = settingsQuery.data

  // Populate once the row arrives, and again after a save so the form's notion
  // of "clean" matches what is actually stored.
  useEffect(() => {
    if (!settings) return

    reset({
      business_name: settings.business_name,
      legal_name: settings.legal_name,
      registration_number: settings.registration_number ?? '',
      public_email: settings.public_email,
      reply_to_email: settings.reply_to_email,
      phone: settings.phone ?? '',
      website_url: settings.website_url,

      vat_registered: settings.vat_registered,
      vat_number: settings.vat_number ?? '',
      vat_rate_pct: Number(settings.vat_rate) * 100,

      home_base_label: settings.home_base_label,
      home_base_address: settings.home_base_address,
      home_base_lat: Number(settings.home_base_lat),
      home_base_lng: Number(settings.home_base_lng),

      default_quote_validity_days: settings.default_quote_validity_days,
      default_deposit_pct: Number(settings.default_deposit_pct) * 100,
      default_balance_due_days_before_event: settings.default_balance_due_days_before_event,
      quote_number_prefix: settings.quote_number_prefix,
      invoice_number_prefix: settings.invoice_number_prefix,

      photo_delivery_weeks: settings.photo_delivery_weeks,
      film_delivery_weeks: settings.film_delivery_weeks,

      reminder_days_before: settings.reminder_days_before,
      reminder_on_due_date: settings.reminder_on_due_date,
      reminder_overdue_every_days: settings.reminder_overdue_every_days,
      reminder_overdue_max: settings.reminder_overdue_max,

      bank_account_name: settings.bank_account_name ?? '',
      bank_name: settings.bank_name ?? '',
      bank_branch_code: settings.bank_branch_code ?? '',
      bank_account_number: settings.bank_account_number ?? '',
      bank_account_type: settings.bank_account_type ?? '',
    }, { keepDirtyValues: true })
  }, [settings, reset])

  if (settingsQuery.isPending) return <Loading label="Loading settings" />

  if (settingsQuery.isError) {
    return (
      <div className="pt-6">
        <ErrorState
          title="Settings could not load"
          detail={describeError(settingsQuery.error)}
          onRetry={() => void settingsQuery.refetch()}
        />
      </div>
    )
  }

  const vatRegistered = watch('vat_registered')

  const onSubmit = handleSubmit(async (values) => {
    const parsed = settingsSchema.parse(values)
    await update.mutateAsync(toRowUpdate(parsed))
    reset(values)
    toast.success('Settings saved')
  }, () => {
    toast.error('Not saved yet', { description: 'Check the highlighted fields.' })
  })

  return (
    <form noValidate onSubmit={(event) => void onSubmit(event)} className="pb-24">
      {isUnconfigured(settings) ? (
        <Notice className="mt-5" tone="amber" title="These are starting values, not your details">
          Nothing on this screen has been saved yet. The deposit, VAT rate and home base below are placeholders —
          work through them once and every quote after that will be right.
        </Notice>
      ) : null}

      {!isOwner ? (
        <Notice className="mt-5" tone="amber" title="Read only">
          Only the owner can change these. Ask Jena if something here is wrong.
        </Notice>
      ) : null}

      <fieldset disabled={!isOwner || update.isPending} className="contents">
        <Section title="Identity" description="How the business names itself on a quote, a contract and an invoice.">
          <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
            <Field label="Trading name" required error={errors.business_name?.message} hint="The name clients see.">
              <Input {...register('business_name')} />
            </Field>
            <Field label="Legal name" required error={errors.legal_name?.message} hint="The registered entity, if it differs.">
              <Input {...register('legal_name')} />
            </Field>
            <Field label="Registration number" error={errors.registration_number?.message}>
              <Input {...register('registration_number')} />
            </Field>
            <Field label="Website" required error={errors.website_url?.message}>
              <Input type="url" {...register('website_url')} />
            </Field>
            <Field label="Public email" required error={errors.public_email?.message} hint="Printed on documents.">
              <Input type="email" {...register('public_email')} />
            </Field>
            <Field label="Reply-to email" required error={errors.reply_to_email?.message} hint="Where client replies land.">
              <Input type="email" {...register('reply_to_email')} />
            </Field>
            <Field label="Phone" error={errors.phone?.message}>
              <Input type="tel" {...register('phone')} />
            </Field>
          </div>
        </Section>

        <Section title="Tax" description="Whether VAT is added to a quote, and at what rate.">
          <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Checkbox
                label="The business is registered for VAT"
                description="Leave this off until SARS registration is confirmed. Turning it on adds a VAT line to every new quote."
                {...register('vat_registered')}
              />
            </div>

            {vatRegistered ? (
              <>
                <Field label="VAT number" required error={errors.vat_number?.message}>
                  <Input {...register('vat_number')} />
                </Field>
                <Field label="VAT rate" required error={errors.vat_rate_pct?.message} aside="South Africa is 15%">
                  <Affixed suffix="%">
                    <Input type="number" step="0.01" min="0" max="99" {...register('vat_rate_pct')} />
                  </Affixed>
                </Field>
              </>
            ) : null}
          </div>
        </Section>

        <Section
          title="Travel origin"
          description="Every distance on a quote is measured from this point. Get the coordinates right and travel prices itself."
        >
          <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
            <Field label="Name" required error={errors.home_base_label?.message} hint="Shown on screen, e.g. Somerset West.">
              <Input {...register('home_base_label')} />
            </Field>
            <Field label="Full address" required error={errors.home_base_address?.message}>
              <Input {...register('home_base_address')} />
            </Field>
            <Field
              label="Latitude"
              required
              error={errors.home_base_lat?.message}
              hint="Right-click the spot in Google Maps and copy the first number."
            >
              <Input type="number" step="0.000001" {...register('home_base_lat')} />
            </Field>
            <Field label="Longitude" required error={errors.home_base_lng?.message} hint="The second number.">
              <Input type="number" step="0.000001" {...register('home_base_lng')} />
            </Field>
          </div>
        </Section>

        <Section title="Quoting defaults" description="Applied to a new quote. Any of them can be overridden on the quote itself.">
          <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
            <Field label="A quote stays valid for" required error={errors.default_quote_validity_days?.message}>
              <Affixed suffix="days">
                <Input type="number" min="1" max="365" {...register('default_quote_validity_days')} />
              </Affixed>
            </Field>
            <Field label="Deposit" required error={errors.default_deposit_pct?.message} hint="The share of the total taken to hold the date.">
              <Affixed suffix="% of the total">
                <Input type="number" step="1" min="0" max="100" {...register('default_deposit_pct')} />
              </Affixed>
            </Field>
            <Field label="Balance falls due" required error={errors.default_balance_due_days_before_event?.message}>
              <Affixed suffix="days before the wedding">
                <Input type="number" min="0" max="365" {...register('default_balance_due_days_before_event')} />
              </Affixed>
            </Field>
            <div className="grid grid-cols-2 gap-x-6">
              <Field label="Quote prefix" required error={errors.quote_number_prefix?.message} hint="e.g. Q-0001">
                <Input {...register('quote_number_prefix')} />
              </Field>
              <Field label="Invoice prefix" required error={errors.invoice_number_prefix?.message} hint="e.g. DLC0001">
                <Input {...register('invoice_number_prefix')} />
              </Field>
            </div>
          </div>
        </Section>

        <Section title="Delivery" description="The promised delivery, counted from the day. Set on each job when its quote is accepted, and editable there.">
          <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
            <Field label="Photos" required error={errors.photo_delivery_weeks?.message}>
              <Affixed suffix="weeks after">
                <Input type="number" min="1" max="52" {...register('photo_delivery_weeks')} />
              </Affixed>
            </Field>
            <Field label="Film" required error={errors.film_delivery_weeks?.message}>
              <Affixed suffix="weeks after">
                <Input type="number" min="1" max="52" {...register('film_delivery_weeks')} />
              </Affixed>
            </Field>
          </div>
        </Section>

        <Section
          title="Payment reminders"
          description="Used once automated emails are switched on. Studio Ninja sent one every two days once a payment was late; this sends a few, gently."
        >
          <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
            <Field label="First reminder" required error={errors.reminder_days_before?.message}>
              <Affixed suffix="days before it is due">
                <Input type="number" min="0" max="60" {...register('reminder_days_before')} />
              </Affixed>
            </Field>
            <Checkbox label="Remind on the day it is due" {...register('reminder_on_due_date')} />
            <Field label="Once late, remind every" required error={errors.reminder_overdue_every_days?.message}>
              <Affixed suffix="days">
                <Input type="number" min="2" max="60" {...register('reminder_overdue_every_days')} />
              </Affixed>
            </Field>
            <Field label="Late reminders at most" required error={errors.reminder_overdue_max?.message} hint="After that, a task for you instead.">
              <Affixed suffix="times">
                <Input type="number" min="0" max="10" {...register('reminder_overdue_max')} />
              </Affixed>
            </Field>
          </div>
        </Section>

        <Section title="Banking" description="Printed on every invoice so a client can pay without asking.">
          <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
            <Field label="Account name" error={errors.bank_account_name?.message}>
              <Input {...register('bank_account_name')} />
            </Field>
            <Field label="Bank" error={errors.bank_name?.message}>
              <Input {...register('bank_name')} />
            </Field>
            <Field label="Account number" error={errors.bank_account_number?.message}>
              <Input inputMode="numeric" {...register('bank_account_number')} />
            </Field>
            <Field label="Branch code" error={errors.bank_branch_code?.message}>
              <Input inputMode="numeric" {...register('bank_branch_code')} />
            </Field>
            <Field label="Account type" error={errors.bank_account_type?.message} hint="Cheque, savings, business.">
              <Input {...register('bank_account_type')} />
            </Field>
          </div>
        </Section>
      </fieldset>

      {isOwner ? (
        <div className="sticky bottom-0 -mx-5 flex items-center justify-end gap-3 border-t border-t-[var(--border-hairline)] bg-cream/95 px-5 py-3 backdrop-blur-sm lg:-mx-8 lg:px-8">
          {isDirty ? <span className="label mr-auto text-amber">Unsaved changes</span> : null}
          <Button variant="ghost" disabled={!isDirty} onClick={() => settings && reset()}>
            Discard
          </Button>
          <Button type="submit" variant="primary" loading={update.isPending} loadingLabel="Saving" disabled={!isDirty}>
            Save settings
          </Button>
        </div>
      ) : null}
    </form>
  )
}
